import { Server } from "lucide-react";
import gcoreLogo from "@/assets/gcore.webp";
import gcpLogo from "@/assets/gcp.webp";
import nebiusLogo from "@/assets/nebius.webp";
import { cn } from "@/lib/utils";

interface ProviderLogoProps {
  provider: string;
  className?: string;
  /** Show text label next to the logo */
  showLabel?: boolean;
}

const providerMeta: Record<
  string,
  { label: string; logo: string | null; hideLabel?: boolean; badgeClass: string }
> = {
  gcp: {
    label: "GCP",
    logo: gcpLogo,
    badgeClass: "border-blue-500/20 bg-blue-500/5 text-blue-700 dark:text-blue-400",
  },
  nebius: {
    label: "Nebius",
    logo: nebiusLogo,
    hideLabel: true,
    badgeClass: "border-violet-500/20 bg-violet-500/5 text-violet-700 dark:text-violet-400",
  },
  gcore: {
    label: "Gcore",
    logo: gcoreLogo,
    hideLabel: true,
    badgeClass: "border-orange-500/20 bg-orange-500/5",
  },
  baremetal: {
    label: "Bare Metal",
    logo: null,
    badgeClass: "border-border bg-muted text-muted-foreground",
  },
  bare_metal: {
    label: "Bare Metal",
    logo: null,
    badgeClass: "border-border bg-muted text-muted-foreground",
  },
  highrise: {
    label: "Highrise",
    logo: null,
    badgeClass: "border-teal-500/30 bg-teal-500/10 text-teal-700 dark:text-teal-400",
  },
};

/** Display label for a provider id (e.g. "gcore" → "Gcore"), falling back to the raw id. */
export function providerLabel(provider: string): string {
  return providerMeta[provider]?.label ?? provider;
}

/**
 * Reusable provider badge with logo image.
 * Used in machine tables, filter pills, spot pool cards.
 */
export function ProviderLogo({ provider, className, showLabel = true }: ProviderLogoProps) {
  const meta = providerMeta[provider] ?? {
    label: provider,
    logo: null,
    badgeClass: "border-border bg-muted text-muted-foreground",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-medium",
        meta.badgeClass,
        className,
      )}
    >
      {meta.logo ? (
        <img src={meta.logo} alt={meta.label} className="h-3.5 w-auto" />
      ) : (
        <Server className="size-3 shrink-0" />
      )}
      {showLabel && !meta.hideLabel && meta.label}
    </span>
  );
}

/** Just the logo icon, no badge wrapper — for filter pills, inline use */
export function ProviderIcon({ provider, className }: { provider: string; className?: string }) {
  const meta = providerMeta[provider];
  if (meta?.logo) {
    return (
      <img
        src={meta.logo}
        alt={meta?.label ?? provider}
        className={cn("h-3.5 w-auto", className)}
      />
    );
  }
  return <Server className={cn("size-3.5", className)} />;
}
